import Axios from "axios";
import {
  call,
  cancelled,
  delay,
  fork,
  put,
  select,
  takeLatest,
} from "redux-saga/effects";
import * as API from "../../api/api";
import { setHasRights } from "../auth";
import { selectReportsFilter } from "../filters/selectors";
import {
  loadEditableRecommendationsActions,
  removeAllEditableRecommendations,
  saveEditableRecommendationsActions,
} from "./recommendationsEditable";
import { toast } from "../../components/FluentToast";
import i18next from "i18next";

export function* loadEditableRecommendationsSaga() {
  yield takeLatest(
    loadEditableRecommendationsActions.trigger,
    function* (
      a: ReturnType<typeof loadEditableRecommendationsActions.trigger>
    ) {
      const { skip, top, reset } = a.payload;
      if (reset) {
        yield put(removeAllEditableRecommendations());
      }
      yield delay(300);
      const cancelSource = Axios.CancelToken.source();
      const clientsFilter = yield select((s) => s.filters.clientsFilter);
      const locationsFilter = yield select((s) => s.filters.locationsFilter);
      const reportsFilter = selectReportsFilter(yield select());
      const search = yield select((s) => s.recommendationsPage.search);
      const sort = yield select((s) => s.recommendationsEditable.sort);
      const columns = yield select((s) => s.recommendationsPage.columns);
      const taxonomy = yield select((s) => s.taxonomy);

      try {
        yield put(loadEditableRecommendationsActions.loading());
        let res = yield call(API.searchRecommendations as any, {
          top,
          skip,
          reset,
          clientsFilter,
          locationsFilter,
          reportsFilter,
          search,
          sortDir: sort.dir,
          sortKey: sort.key,
          cancelToken: cancelSource.token,
          columns,
          taxonomy,
        });

        yield put(
          loadEditableRecommendationsActions.success({ res: res.data, reset })
        );
      } catch (err: any) {
        console.log(err);
        if (err.response?.status === 403) {
          yield put(setHasRights(false));
        }
        yield put(loadEditableRecommendationsActions.error(err));
      } finally {
        if (yield cancelled()) {
          cancelSource.cancel();
        }
      }
    }
  );
}

// Fields that can be sent to the backend for batch update
const BATCH_UPDATE_FIELDS = [
  "recommendationId",
  "reportId",
  "locationId",
  "improvementPotentialCode",
  "statusCompanyCode",
  "typeCode",
  "subCategory1Code",
  "weighting",
  "priorityCompanyCode",
  "priorityCode",
  "improvementPotentialCodeAsIf",
  "costEstimateCode",
  "hideFromInsurer",
  "comment",
];

export function* saveEditableRecommendationsSaga() {
  yield takeLatest(
    saveEditableRecommendationsActions.trigger,
    function* (
      a: ReturnType<typeof saveEditableRecommendationsActions.trigger>
    ) {
      const { recommendations } = a.payload;

      try {
        yield put(saveEditableRecommendationsActions.loading());

        // Filter recommendations to only include fields we work with
        const filteredRecommendations = recommendations.map((rec) => {
          const filtered: Record<string, any> = {};
          BATCH_UPDATE_FIELDS.forEach((field) => {
            if (field in rec) {
              filtered[field] = rec[field as keyof typeof rec];
            }
          });
          return filtered;
        });

        // Call batch update API with filtered data
        yield call(
          API.batchUpdateRecommendations,
          filteredRecommendations as any
        );

        const savedIds = recommendations.map((r) => r.recommendationId);
        yield put(saveEditableRecommendationsActions.success({ savedIds }));

        toast.success(i18next.t("ram.recommendationsEditable.saveSuccess"));
      } catch (err: any) {
        console.log(err);
        if (err.response?.status === 403) {
          yield put(setHasRights(false));
        }
        toast.error(
          i18next.t("ram.recommendationsEditable.saveError") ||
            "Failed to save recommendations"
        );
        yield put(saveEditableRecommendationsActions.error(err));
      }
    }
  );
}

export function* recommendationsEditableSaga() {
  yield fork(loadEditableRecommendationsSaga);
  yield fork(saveEditableRecommendationsSaga);
}
